<template>
    <login-layout
        eyebrow="Participantes"
        titulo="Certificados y calificaciones"
        subtitulo="Ingresa con tu número de identidad para ver tu historial de cursos, tus notas, certificados y constancias; o verifica un certificado con su número de registro.">

        <div class="segmentos" role="tablist" aria-label="Forma de ingreso">
            <button type="button" role="tab"
                    :class="{activo: modo === 'identidad'}"
                    :aria-selected="modo === 'identidad'"
                    @click="activarEntrarNumeroIdentidad">
                <v-icon>badge</v-icon> Número de identidad
            </button>
            <button type="button" role="tab"
                    :class="{activo: modo === 'registro'}"
                    :aria-selected="modo === 'registro'"
                    @click="activarEntrarNumeroRegistroCurso">
                <v-icon>confirmation_number</v-icon> Número de registro
            </button>
        </div>

        <v-form v-show="modo === 'identidad'" ref="formUsuario" v-model="formValid" @submit.prevent="loginNumeroIdentidad">
            <v-text-field
                class="campo"
                outlined
                prepend-inner-icon="badge"
                type="text"
                inputmode="numeric"
                autocomplete="username"
                :label="usuarioForm.usuario.label"
                :placeholder="usuarioForm.usuario.placeholder"
                v-model="usuarioModel.usuario"
                :rules="usuarioForm.usuario.rules"
                @keyup.enter="loginNumeroIdentidad"
            />
            <p class="campo__ayuda">
                Escribe los 13 dígitos de tu tarjeta de identidad, sin guiones ni espacios
                (por ejemplo, <b>0801199912345</b>). Debe ser la misma identidad con la que te inscribiste en el curso.
            </p>

            <v-btn class="boton-principal" depressed ref="formUsuarioBtn"
                   :loading="loading" @click="loginNumeroIdentidad">
                Ingresar
                <v-icon right>arrow_forward</v-icon>
            </v-btn>
        </v-form>

        <!-- Verificación por número de registro: no inicia sesión, muestra el certificado verificado. -->
        <v-form v-show="modo === 'registro' && !verificado" ref="formCurso" v-model="formValid" @submit.prevent="loginNumeroRegistroCurso">
            <certificado-guia/>

            <v-text-field
                class="campo"
                outlined
                prepend-inner-icon="confirmation_number"
                type="text"
                inputmode="numeric"
                :label="cursoForm.numero_registro.label"
                :placeholder="cursoForm.numero_registro.placeholder"
                v-model="cursoModel.numero_registro"
                :rules="cursoForm.numero_registro.rules"
                @keyup.enter="loginNumeroRegistroCurso"
            />
            <p class="campo__ayuda">
                Verás los datos del certificado tal como fueron emitidos por INFOP y podrás descargarlo.
                Sirve para comprobar que un certificado es auténtico sin necesidad de iniciar sesión.
            </p>

            <v-btn class="boton-principal" depressed
                   :loading="loading" @click="loginNumeroRegistroCurso">
                Verificar certificado
                <v-icon right>verified</v-icon>
            </v-btn>
        </v-form>

        <div v-if="modo === 'registro' && verificado" class="verificado">
            <div class="verificado__sello">
                <v-icon>verified</v-icon>
                <div>
                    <strong>Certificado verificado</strong>
                    <span>Registro No. {{ verificado.curso.registro }} · emitido por INFOP</span>
                </div>
            </div>

            <dl class="verificado__datos">
                <div><dt>Participante</dt><dd>{{ verificado.persona.nombre }} {{ verificado.persona.apellido }}</dd></div>
                <div><dt>Curso</dt><dd>{{ verificado.curso.nombre }}</dd></div>
                <div><dt>Periodo</dt><dd>{{ verificado.curso.fecha_inicio | fechaESP }} – {{ verificado.curso.fecha_finalizacion | fechaESP }}<template v-if="Number(verificado.curso.horas_curso)"> · {{ Number(verificado.curso.horas_curso) }} horas</template></dd></div>
                <div v-if="verificado.curso.tutor"><dt>Tutor</dt><dd>{{ verificado.curso.tutor }}</dd></div>
                <div v-if="verificado.unidadOperativa.unidad_operativa"><dt>Unidad operativa</dt><dd>{{ verificado.unidadOperativa.unidad_operativa }}</dd></div>
                <div v-if="verificado.unidadOperativa.modo_formacion"><dt>Modo de formación</dt><dd>{{ verificado.unidadOperativa.modo_formacion }}</dd></div>
            </dl>

            <v-btn class="boton-principal" depressed :href="urlCertificado" target="_blank" rel="noopener">
                Descargar certificado
                <v-icon right>download</v-icon>
            </v-btn>

            <div class="verificado__acciones">
                <button type="button" @click="verificarOtro"><v-icon>search</v-icon> Verificar otro registro</button>
                <button type="button" @click="activarEntrarNumeroIdentidad"><v-icon>badge</v-icon> Ver todos mis cursos</button>
            </div>
        </div>

        <!-- En el teléfono el panel azul no muestra el botón: va aquí, bajo el formulario. -->
        <div class="tutorial-movil">
            <tutorial-video variante="claro"/>
        </div>

        <template #ayuda>
            <tutorial-video variante="oscuro"/>
        </template>
    </login-layout>
</template>

<script>
import Ls from "@/services/ls";
import LoginLayout from "@/components/Login/LoginLayout.vue";
import TutorialVideo from "@/components/Ayuda/TutorialVideo.vue";
import CertificadoGuia from "@/components/Login/CertificadoGuia.vue";

export default {
    name: 'LoginCertificados',
    components: {LoginLayout, TutorialVideo, CertificadoGuia},
    data() {
        return {
            formValid: true,
            loading: false,
            // 'identidad' | 'registro'
            modo: 'identidad',
            // Resultado de la verificación por número de registro.
            verificado: null,
            usuarioModel: {
                usuario: null,
            },
            cursoModel: {
                numero_registro: null
            },
            usuarioForm: {
                usuario: {
                    label: 'Número de identidad',
                    placeholder: '0801199912345',
                    rules: [(v) => !!v || 'Campo requerido'],
                },
            },
            cursoForm: {
                numero_registro: {
                    label: 'Número de registro',
                    placeholder: '2658171',
                    rules: [
                        (v) => !!v || 'Campo requerido',
                        (v) => !v || /^\d+$/.test(String(v).trim()) || 'Solo dígitos, sin letras ni espacios',
                    ],
                },
            },
        }
    },
    computed: {
        urlCertificado() {
            return this.verificado ? `${this.baseURLApi}/certificados/generate/${this.verificado.token_certificado}` : null;
        },
    },
    created() {
        // validar si hay session
        if (Ls.get("token")) {
            this.$router.push({name: 'MisCursosHistorial'});
        }
    },
    methods: {
        activarEntrarNumeroIdentidad() {
            this.modo = 'identidad';
            this.cursoModel.numero_registro = null;
        },
        activarEntrarNumeroRegistroCurso() {
            this.modo = 'registro';
            this.usuarioModel.usuario = null;
        },
        verificarOtro() {
            this.verificado = null;
            this.cursoModel.numero_registro = null;
        },
        loginNumeroIdentidad() {
            // Se compara por model para que permita ingresar por el otro metodo y permita iniciar sesion
            if (this.usuarioModel.usuario != null) {

                this.loading = true;

                let params = {
                    usuario: this.usuarioModel.usuario,
                    password: this.usuarioModel.usuario,
                };

                window.axios.post('/login', params).then(async response => {
                    if (response.data.status == "active") {

                        let data = response.data.data;

                        Ls.set('token', response.data.token);
                        Ls.set('tipo_persona', data.cat_tipo_persona_id);
                        Ls.set('nombre', data.nombre);
                        Ls.set('apellido', data.apellido);
                        Ls.set('rol', 1);
                        Ls.remove('curso_id');

                        await this.$router.push({name: 'MisCursosHistorial'});

                    } else if (response.data.status == "inactive") {

                        this.$toast.error(response.data.message);
                    } else if (response.data.status == "invalid") {

                        this.$toast.error(response.data.message);
                    } else if (response.data.status == "nodata") {

                        this.$toast.error(response.data.message);
                    }
                })
                    .catch(error => {
                        console.log(error)
                        this.$toast.error("No se pudo conectar");
                    })
                    .finally(() => {
                        this.loading = false;
                    })
            } else {
                this.$refs.formUsuario.validate();
                this.$toast.warning("Escriba el Número de Identidad.");
            }
        },
        loginNumeroRegistroCurso() {
            if (!this.$refs.formCurso.validate()) {
                this.$toast.warning("Escriba el número de registro.");
                return;
            }

            this.loading = true;
            const params = {numero_registro: String(this.cursoModel.numero_registro).trim()};

            window.axios.post('/cursos/validar_numero_registro', params).then(response => {
                if (response.data.success) {
                    this.verificado = response.data.data;
                } else {
                    this.$toast.error(response.data.message, {timeout: 10000});
                }
            })
                .catch(error => {
                    console.log(error)
                    this.$toast.error("No se pudo conectar");
                })
                .finally(() => {
                    this.loading = false;
                })
        },
    }
}
</script>

<style lang="scss" scoped>
/* El botón del tutorial vive en el panel azul; en pantallas pequeñas ese panel se recorta y pasa aquí. */
.tutorial-movil {
    display: none;
    margin-top: 16px;
}

@media (max-width: 959px) {
    .tutorial-movil {
        display: block;
    }
}

.verificado__sello {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border-radius: 12px;
    background: #e3f5ec;
    border: 1px solid #b9e4cf;
    margin-bottom: 14px;

    .v-icon { color: #12704d; font-size: 30px; }

    strong { display: block; color: #0e5a3d; font-size: 15px; }
    span { font-size: 12.5px; color: #2f6b52; }
}

.verificado__datos {
    margin: 0 0 16px;
    border: 1px solid #dce1ec;
    border-radius: 12px;
    overflow: hidden;

    > div {
        display: grid;
        grid-template-columns: 120px 1fr;
        gap: 10px;
        padding: 9px 12px;
        font-size: 13.5px;

        & + div { border-top: 1px solid #e9edf5; }
    }

    dt { color: #5b6480; font-weight: 500; }
    dd { margin: 0; color: #1b2340; font-weight: 500; overflow-wrap: anywhere; }
}

.verificado__acciones {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 8px 16px;
    margin-top: 14px;
    font-size: 13px;

    button {
        appearance: none;
        background: none;
        border: 0;
        padding: 0;
        font: inherit;
        color: #203d89;
        font-weight: 500;
        cursor: pointer;
        display: inline-flex;
        align-items: center;
        gap: 4px;

        .v-icon { font-size: 16px; color: inherit; }
        &:hover { text-decoration: underline; }
    }
}
</style>
